/** @format */

import axios from 'axios'
import { reportAPIError, addCustomBreadcrumb, trackFileOperation } from '@/config/sentryConfig'
import { useAuthStore } from '@/stores/auth'
import ziggyRoute from '../services/ziggy.js'
import ValidationErrorHandlingService from '../services/ValidationErrorHandlingService.js'
import { getToastDuration } from '@/config/toastConfig.js'
import { expireTableFilters } from '@/helpers/retainedTableFilters'

const USER_TYPE_ROUTES = {
	'profile.update': {
		external: 'profile.update',
		internal: 'internal.profile.update',
	},
	'profile.show': {
		external: 'profile.show',
		internal: 'internal.profile.show',
	},
}

const ApiService = {
	_app: null,
	_router: null,
	_toast: null,
	_api: null,
	_validationErrorHandler: null,

	init(app) {
		this._app = app

		this._router = app.config.globalProperties.$router

		this._toast = app.config.globalProperties.$toast

		this._validationErrorHandler = new ValidationErrorHandlingService(
			this._router,
			this._toast,
			app.config.globalProperties.$modalStore,
		)

		this._api = axios.create({
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
			withCredentials: true,
		})

		this._api.interceptors.request.use((config) => {
			const token = document.cookie
				.split('; ')
				.find((row) => row.startsWith('XSRF-TOKEN='))
				?.split('=')[1]

			if (token) {
				config.headers['X-XSRF-TOKEN'] = decodeURIComponent(token)
			}

			const shouldLogBreadcrumb = (config) => {
				const method = config.method?.toUpperCase()
				const url = config.url || ''

				if (method !== 'GET') {
					return true
				}

				const importantGetEndpoints = [
					'user-data',
					'auth',
					'login',
					'logout',
					'click_finance',
					'contract',
					'invoice',
				]

				return importantGetEndpoints.some((endpoint) => url.includes(endpoint))
			}

			if (shouldLogBreadcrumb(config)) {
				addCustomBreadcrumb(
					`API ${config.method?.toUpperCase()} ${config.url}`,
					'api.request',
					{
						method: config.method,
						url: config.url,
						params: config.params,
					},
				)
			}

			return config
		})

		this._api.interceptors.response.use(
			(response) => response,
			(error) => {
				if (error.response?.status > 500) {
					reportAPIError(error, error.config?.url, error.config?.method)
				}
				return this._handleError(error)
			},
		)
	},

	getRouteForUserType(routeName, userType = 'external') {
		if (USER_TYPE_ROUTES[routeName]) {
			return USER_TYPE_ROUTES[routeName][userType] || routeName
		}
		return routeName
	},

	async _resolveUrl(url, params = {}) {
		if (typeof url === 'string' && (url.startsWith('http') || url.startsWith('/'))) {
			return url
		}
		const namedRoute = await ziggyRoute(url, params)
		return namedRoute
	},

	async get(url, params = {}) {
		const resolvedUrl = await this._resolveUrl(url, params)
		return this._api.get(resolvedUrl)
	},

	async post(url, data = {}, params = {}) {
		try {
			const resolvedUrl = await this._resolveUrl(url, params)
			return this._api.post(resolvedUrl, data)
		} catch (error) {
			console.log(error)
			return Promise.reject(error)
		}
	},

	async patch(url, data = {}, params = {}) {
		const resolvedUrl = await this._resolveUrl(url, params)
		return this._api.patch(resolvedUrl, data)
	},

	async delete(url, params = {}) {
		const resolvedUrl = await this._resolveUrl(url, params)
		return this._api.delete(resolvedUrl)
	},

	async downloadFile(url, filename, params = {}) {
		const fileTracker = trackFileOperation('download', filename || 'unknown', {
			url,
			params: Object.keys(params),
		})

		try {
			const resolvedUrl = await this._resolveUrl(url, params)

			const response = await this._api.get(resolvedUrl, {
				responseType: 'blob',
			})

			const contentType = response.headers['content-type']

			const extensionMap = {
				'application/pdf': '.pdf',
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
				'application/vnd.ms-excel': '.xls',
				'application/msword': '.doc',
				'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
				'image/jpeg': '.jpg',
				'image/png': '.png',
				'image/gif': '.gif',
				'text/plain': '.txt',
				'text/csv': '.csv',
				'application/json': '.json',
			}

			const extension = extensionMap[contentType] || ''

			const blob = new Blob([response.data], { type: contentType })
			const downloadUrl = window.URL.createObjectURL(blob)

			const anchor = document.createElement('a')
			anchor.href = downloadUrl

			let finalFilename = filename
			if (!finalFilename) {
				const contentDisposition = response.headers['content-disposition']
				if (contentDisposition) {
					const filenameMatch = contentDisposition.match(
						/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
					)
					if (filenameMatch && filenameMatch[1]) {
						finalFilename = filenameMatch[1].replace(/['"]/g, '')
					}
				}
			}

			if (!finalFilename) {
				finalFilename = `download-${new Date().getTime()}`
			}

			if (extension && !finalFilename.endsWith(extension)) {
				finalFilename += extension
			}

			anchor.download = finalFilename

			document.body.appendChild(anchor)
			anchor.click()
			window.URL.revokeObjectURL(downloadUrl)
			document.body.removeChild(anchor)

			if (fileTracker) {
				fileTracker.success({
					finalFilename,
					contentType,
					fileSize: response.data.size,
				})
			}

			return response
		} catch (error) {
			console.error('Download failed:', error)

			if (fileTracker) {
				fileTracker.error(error, {
					errorType: error.response?.status || 'network_error',
				})
			}

			const errorMessage = 'The file could not be downloaded. Please try again.'
			this._toast.add({
				severity: 'error',
				summary: 'Download Failed',
				detail: errorMessage,
				life: getToastDuration(errorMessage, 'error'),
			})

			return Promise.reject(error)
		}
	},

	async _pushToPageWithMessage(page, message) {
		await this._router.push({ name: page })
		this._toast.add({
			severity: 'error',
			summary: 'Error',
			detail: message,
			life: getToastDuration(message, 'error'),
		})
	},

	_handleError(error) {
		const { response } = error

		switch (response?.status) {
			case 401:
			case 419:
				expireTableFilters()
				const authStore = useAuthStore()
				authStore.userId = null
				const loginRoute =
					authStore.currentUserType === 'internal' ? 'InternalLogin' : 'login'
				const errorMessage = response?.status === 419 ? '§error.419' : '§error.401'
				this._pushToPageWithMessage(loginRoute, errorMessage)
				break
			case 403:
				this._router.push({ name: 'home' })
				break
			case 404:
				this._pushToPageWithMessage('PageNotFound', response?.data?.message || 'Forbidden')
				break
			case 422:
				if (this._validationErrorHandler) {
					this._validationErrorHandler.handleValidationError(error)
				}
				break
			default:
				const errorDetail =
					response?.data?.message ||
					response?.statusText ||
					'An unexpected error occurred'
				this._toast.add({
					severity: 'error',
					summary: 'Error',
					detail: errorDetail,
					life: getToastDuration(errorDetail, 'error'),
				})
		}

		return Promise.reject(error)
	},
}

export default ApiService
